import { makeUserAccountProfileUrl } from '@/main/factories/http/api-url-factory'
import { AxiosHttpClient } from './axios-http-client'

export type Reachability = 'offline' | 'reachable' | 'unreachable'

const PROBE_TIMEOUT_MS = 3000

/**
 * GET leve, idempotente, autenticado e CORS-enabled que a SPA já chama em toda
 * sessão. Serve de "ping" da API: é DELIBERADAMENTE diferente da request que
 * falhou (método/payload/path) — é justamente esse contraste que revela um
 * bloqueio seletivo (ex.: DLP/proxy que barra só um upload). Não existe um
 * endpoint de health dedicado; /profile é o GET neutro mais confiável.
 */
export const reachabilityProbeUrl = (): string => makeUserAccountProfileUrl()

/**
 * Discrimina, após uma NetworkError, se a falha é do dispositivo/conexão ou se
 * a nossa API está alcançável (e portanto a request específica foi barrada por
 * um intermediário — ex.: política de proxy/DLP corporativo).
 *
 * - `offline`     → navegador sem rede (curto-circuito).
 * - `reachable`   → a API respondeu ao probe (qualquer status, mesmo 401/403/404):
 *                   a conexão funciona, mas a request original foi bloqueada.
 * - `unreachable` → o probe também não obteve resposta (sem conexão com a API).
 *
 * Reusa o AxiosHttpClient para herdar interceptors/headers e o mesmo tratamento
 * de falha de rede (sem resposta → lança NetworkError, capturado abaixo).
 */
export async function probeApiReachable(
  probeUrl: string = reachabilityProbeUrl()
): Promise<Reachability> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return 'offline'
  }

  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS)

  try {
    // Qualquer resposta HTTP (mesmo 401/403/404) significa API alcançável.
    await new AxiosHttpClient().request({
      url: probeUrl,
      method: 'get',
      withCredentials: true,
      signal: controller.signal
    })
    return 'reachable'
  } catch {
    // NetworkError (sem resposta), timeout/abort ou falha inesperada: não foi
    // possível confirmar alcance.
    return 'unreachable'
  } finally {
    clearTimeout(timeout)
  }
}

/**
 * Memoiza o probe por instância de erro: o reporter (query-client) e o helper
 * de mensagem (presentation) reagem ao MESMO NetworkError, então compartilham
 * uma única Promise. WeakMap evita vazamento.
 */
const reachabilityByError = new WeakMap<object, Promise<Reachability>>()

/**
 * Dedup global de probe em andamento, por URL: se várias queries falharem juntas
 * (cada uma com sua instância de NetworkError) durante uma instabilidade de rede,
 * todas reaproveitam o mesmo probe in-flight em vez de inundar a rede com GETs
 * idênticos. Chaveado por `probeUrl` para que sondas de URLs distintas não se
 * misturem. Liberado no settle para que um erro posterior re-sonde.
 */
const inFlightProbes = new Map<string, Promise<Reachability>>()

export function probeReachabilityOnce(
  errorKey: object,
  probeUrl: string = reachabilityProbeUrl()
): Promise<Reachability> {
  const memoized = reachabilityByError.get(errorKey)
  if (memoized) return memoized

  let pending = inFlightProbes.get(probeUrl)
  if (!pending) {
    pending = probeApiReachable(probeUrl).finally(() => {
      inFlightProbes.delete(probeUrl)
    })
    inFlightProbes.set(probeUrl, pending)
  }

  reachabilityByError.set(errorKey, pending)
  return pending
}
